  // MyCertificatesPage — список моих сертификатов.
// Каждая карточка: обложка курса, название, дата выдачи, номер,
// две кнопки скачивания (RU/KK) и кнопка «копировать ссылку верификации».
// Скачивание идёт прямым GET на /api/certificates/:num/pdf?lang=...
// (Auth-токен берётся из localStorage; для PDF делаем через fetch+Blob
// чтобы не плодить вкладок).

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import {
  Award,
  Download,
  FileText,
  Loader2,
  Copy,
  CheckCircle,
  AlertTriangle,
  BookOpen,
  ArrowRight,
} from 'lucide-react';
import { useGetMyCertificatesQuery } from '../../api/apiSlice';
import { COURSE_COVER_FALLBACK_SM, setFallback } from '../../lib/placeholders';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Стримим PDF из API в Blob → создаём object URL → триггерим скачивание.
// Через `<a download>` напрямую нельзя — нужен Authorization header.
async function downloadPdf(certificateNumber, lang, fallbackMsg) {
  const token = localStorage.getItem('accessToken');
  const res = await fetch(`${API_BASE}/certificates/${certificateNumber}/pdf?lang=${lang}`, {
    headers: { Authorization: `Bearer ${token}` },
    credentials: 'include',
  });
  if (!res.ok) {
    let msg = fallbackMsg;
    try { const j = await res.json(); msg = j.message || msg; } catch {}
    throw new Error(msg);
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `Uaqyt-${certificateNumber}-${lang}.pdf`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Освобождаем blob URL через секунду — даём браузеру дочитать.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

const MyCertificatesPage = () => {
  const { t } = useTranslation();
  const { data, isLoading } = useGetMyCertificatesQuery();
  const items = data?.items || [];
  const [downloading, setDownloading] = useState(null); // `${number}-${lang}`

  const handleDownload = async (number, lang) => {
    const key = `${number}-${lang}`;
    setDownloading(key);
    try {
      await downloadPdf(number, lang, t('certificates.downloadError'));
    } catch (err) {
      toast.error(err.message || t('certificates.downloadFailed'));
    } finally {
      setDownloading(null);
    }
  };

  const handleCopy = async (token) => {
    const url = `${window.location.origin}/verify/${token}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success(t('certificates.linkCopied'));
    } catch {
      toast.error(t('certificates.copyFailed'));
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6">
      <div className="mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">
          {t('certificates.pageTitle')}
        </h1>
        <p className="mt-2 text-sm text-gray-500 dark:text-gray-400 max-w-2xl">
          {t('certificates.pageSubtitle')}
        </p>
      </div>

      {items.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50 p-12 text-center">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-amber-100 dark:bg-amber-500/15">
            <Award className="w-7 h-7 text-amber-500" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            {t('certificates.empty')}
          </h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {t('certificates.emptyHint')}
          </p>
          <Link
            to="/courses"
            className="mt-5 inline-flex items-center gap-2 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white px-5 py-2.5 text-sm font-semibold transition"
          >
            <BookOpen className="w-4 h-4" />
            {t('certificates.browseCourses')}
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      ) : (
        <div className="space-y-4">
          {items.map((c) => (
            <article
              key={c._id}
              className={`rounded-2xl border bg-white dark:bg-gray-900 shadow-sm overflow-hidden ${
                c.revoked
                  ? 'border-red-200 dark:border-red-900/40'
                  : 'border-gray-200 dark:border-gray-700'
              }`}
            >
              <div className="flex flex-col sm:flex-row">
                <div className="sm:w-48 h-32 sm:h-auto shrink-0 bg-gradient-to-br from-indigo-500 to-purple-600 relative">
                  <img
                    src={c.course?.coverImageUrl || COURSE_COVER_FALLBACK_SM}
                    alt=""
                    onError={setFallback(COURSE_COVER_FALLBACK_SM)}
                    className="w-full h-full object-cover"
                  />
                  {c.revoked && (
                    <div className="absolute inset-0 bg-red-500/70 flex items-center justify-center">
                      <span className="px-3 py-1 bg-red-700 text-white text-xs font-bold rounded-full uppercase tracking-wider">
                        {t('certificates.revoked')}
                      </span>
                    </div>
                  )}
                </div>

                <div className="flex-1 p-5 sm:p-6 min-w-0">
                  <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
                    <div className="min-w-0 flex-1">
                      <h3 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white break-words">
                        {c.courseTitle}
                      </h3>
                      {c.instructorName && (
                        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                          {t('certificates.instructor')}: {c.instructorName}
                        </p>
                      )}
                    </div>
                    <div className="text-right shrink-0">
                      <div className="text-xs uppercase text-gray-400 tracking-wider">
                        {t('certificates.number')}
                      </div>
                      <div className="font-mono font-bold text-gray-800 dark:text-gray-200 text-sm">
                        {c.certificateNumber}
                      </div>
                    </div>
                  </div>

                  <div className="text-xs text-gray-500 dark:text-gray-400 mb-4">
                    {t('certificates.issuedOn')}:{' '}
                    <span className="font-medium text-gray-700 dark:text-gray-300">
                      {new Date(c.issuedAt).toLocaleDateString(undefined, {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                      })}
                    </span>
                  </div>

                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      disabled={downloading === `${c.certificateNumber}-ru` || c.revoked}
                      onClick={() => handleDownload(c.certificateNumber, 'ru')}
                      className="inline-flex items-center gap-2 rounded-xl bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white px-4 py-2 text-sm font-semibold transition"
                    >
                      {downloading === `${c.certificateNumber}-ru` ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Download className="w-4 h-4" />
                      )}
                      {t('certificates.downloadRu')}
                    </button>
                    <button
                      type="button"
                      disabled={downloading === `${c.certificateNumber}-kk` || c.revoked}
                      onClick={() => handleDownload(c.certificateNumber, 'kk')}
                      className="inline-flex items-center gap-2 rounded-xl border border-indigo-200 dark:border-indigo-900/50 hover:bg-indigo-50 dark:hover:bg-indigo-950/30 disabled:opacity-50 text-indigo-700 dark:text-indigo-300 px-4 py-2 text-sm font-semibold transition"
                    >
                      {downloading === `${c.certificateNumber}-kk` ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <FileText className="w-4 h-4" />
                      )}
                      {t('certificates.downloadKk')}
                    </button>
                    <button
                      type="button"
                      onClick={() => handleCopy(c.verificationToken)}
                      className="inline-flex items-center gap-2 rounded-xl border border-gray-300 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-200 px-4 py-2 text-sm font-medium transition"
                    >
                      <Copy className="w-4 h-4" />
                      {t('certificates.copyLink')}
                    </button>
                  </div>
                </div>
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  );
};

export default MyCertificatesPage;
